import {
  createContext,
  lazy,
  type ReactNode,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import type { ImageViewerRequest } from "../types";

/**
 * Loaded on first open, not on mount.
 *
 * This provider wraps the whole app in `app/root.tsx`, so a static import here
 * put the viewer — and everything it transitively reaches through the
 * `businessObjects` and `documents` barrels — into the entry graph, preloaded
 * on every page load for a modal most sessions never open.
 *
 * `.then(m => ({ default: … }))` because the dialog is a named export; same
 * idiom as the lazy route components in `app/routes/`.
 */
const ImageViewerDialog = lazy(() =>
  import("../components/ImageViewerDialog").then((m) => ({ default: m.ImageViewerDialog })),
);

interface ImageViewerContextValue {
  openImageViewer: (request: ImageViewerRequest) => void;
  closeImageViewer: () => void;
}

const ImageViewerContext = createContext<ImageViewerContextValue | null>(null);

export const ImageViewerProvider = ({ children }: { children: ReactNode }) => {
  const [request, setRequest] = useState<ImageViewerRequest | null>(null);
  // Latches on first open and never resets. The dialog is designed to stay
  // mounted across a `request → null` transition (it owns close/cleanup
  // behaviour keyed on that change), so unmounting it on close would cut that
  // short. Latching defers the chunk without altering the close path.
  const [hasOpened, setHasOpened] = useState(false);

  const openImageViewer = useCallback((next: ImageViewerRequest) => {
    setHasOpened(true);
    setRequest(next);
  }, []);

  const closeImageViewer = useCallback(() => {
    setRequest(null);
  }, []);

  const value = useMemo(
    () => ({
      openImageViewer,
      closeImageViewer,
    }),
    [closeImageViewer, openImageViewer],
  );

  return (
    <ImageViewerContext.Provider value={value}>
      {children}
      {hasOpened ? (
        <Suspense fallback={null}>
          <ImageViewerDialog request={request} onClose={closeImageViewer} />
        </Suspense>
      ) : null}
    </ImageViewerContext.Provider>
  );
};

export const useImageViewer = (): ImageViewerContextValue => {
  const context = useContext(ImageViewerContext);
  if (!context) {
    throw new Error("useImageViewer must be used within ImageViewerProvider");
  }
  return context;
};
